import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface DatePickerProps {
  // Original props
  selected: Date | undefined
  onSelect: (date: Date | undefined) => void
  minDate?: Date
  maxDate?: Date
  className?: string
  // New props (for future use)
  date?: Date
  onDateChange?: (date?: Date) => void
}

export function DatePicker({ 
  // Original props
  selected,
  onSelect,
  minDate,
  maxDate,
  className,
  // New props (for future use)
  date,
  onDateChange
}: DatePickerProps) {
  // Use either the new props or the original ones
  const currentDate = date || selected;
  const handleDateChange = onDateChange || onSelect;
  
  // Add state for popover open/close
  const [open, setOpen] = React.useState(false);
  
  // Handle date selection and close popover
  const handleSelectDate = (date: Date | undefined) => {
    handleDateChange(date);
    setOpen(false);
  };
  
  const years = React.useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 100 }, (_, i) => currentYear - 50 + i)
  }, [])

  const months = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: format(new Date(2000, i), "MMMM")
    }))
  }, [])

  const handleYearChange = (year: string) => {
    if (currentDate) {
      const newDate = new Date(currentDate)
      newDate.setFullYear(parseInt(year))
      handleDateChange(newDate)
    }
  }

  const handleMonthChange = (month: string) => {
    if (currentDate) {
      const newDate = new Date(currentDate)
      newDate.setMonth(parseInt(month))
      handleDateChange(newDate)
    }
  }

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-[280px] justify-start text-left font-normal",
            !currentDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {currentDate ? format(currentDate, "PPP") : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {/* <div className="flex items-center justify-between p-3 border-b">
          <Select
            value={currentDate?.getMonth().toString()}
            onValueChange={handleMonthChange}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((month) => (
                <SelectItem key={month.value} value={month.value.toString()}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={currentDate?.getFullYear().toString()}
            onValueChange={handleYearChange}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div> */}
        <Calendar
          mode="single"
          selected={currentDate}
          onSelect={handleSelectDate}
          initialFocus
          defaultMonth={currentDate}
          disabled={(date) => {
            if (!date) return false
            if (minDate && date < minDate) return true
            if (maxDate && date > maxDate) return true
            return false
          }}
        />
      </PopoverContent>
    </Popover>
  )
}


export default DatePicker;
